import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

interface NavbarProps {
  forceSolid?: boolean;
}

export default function Navbar({ forceSolid = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isSolid = forceSolid || isScrolled;
  const isHomePage = location.pathname === "/";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : isHomePage
            ? "bg-black/20 backdrop-blur-sm"
            : "bg-background-50/95 backdrop-blur-md shadow-sm"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <span className={`font-heading text-2xl md:text-3xl font-bold tracking-tight transition-colors duration-300 ${
              isSolid || !isHomePage ? "text-foreground-900" : "text-white"
            }`}>
              AC<span className="text-primary-400">BUY</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Home */}
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                isSolid || !isHomePage ? "text-foreground-700 hover:text-foreground-900" : "text-white/80 hover:text-white"
              }`}
            >
              <i className="ri-home-4-line text-sm"></i>
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/acbuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                isSolid || !isHomePage ? "text-foreground-700 hover:text-foreground-900" : "text-white/80 hover:text-white"
              }`}
            >
              <i className="ri-book-open-line text-sm"></i>
              Shopping Guide
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  isSolid || !isHomePage ? "text-foreground-700 hover:text-foreground-900" : "text-white/90 hover:text-white"
                }`}
              >
                <i className="ri-grid-line"></i>
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-80 rounded-xl overflow-hidden shadow-lg border bg-background-50 border-background-200">
                  <div className="max-h-[460px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-primary-50 transition-colors duration-150 cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                          <img
                            src={cat.imageUrl}
                            alt={cat.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-800 truncate">{cat.name}</p>
                          <p className="text-xs text-foreground-500 truncate">{cat.description}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Nav Links */}
            <a
              href="#about"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                isSolid || !isHomePage ? "text-foreground-700 hover:text-foreground-900" : "text-white/80 hover:text-white"
              }`}
            >
              About
            </a>
            <a
              href="#faq"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                isSolid || !isHomePage ? "text-foreground-700 hover:text-foreground-900" : "text-white/80 hover:text-white"
              }`}
            >
              FAQ
            </a>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`ml-2 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                isSolid || !isHomePage
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30"
              }`}
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isSolid || !isHomePage ? "text-foreground-800 hover:bg-background-200" : "text-white hover:bg-white/10"
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 rounded-2xl shadow-lg border border-background-200 p-4 mb-4 animate-in slide-in-from-top-2">
            {/* Quick Links */}
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-background-200">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-background-100 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Home
              </Link>
              <Link
                to="/acbuy-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-50 text-primary-700 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                Guide
              </Link>
            </div>

            {/* Categories Grid */}
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Product Categories</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {categories.slice(0, 14).map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-medium text-foreground-800 block truncate">{cat.name}</span>
                    <span className="text-[10px] text-foreground-500 truncate block">{cat.description}</span>
                  </div>
                </a>
              ))}
            </div>

            <div className="border-t border-background-200 pt-3 flex flex-col gap-1">
              <a
                href="#about"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 cursor-pointer rounded-lg hover:bg-background-100"
              >
                About
              </a>
              <a
                href="#faq"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 cursor-pointer rounded-lg hover:bg-background-100"
              >
                FAQ
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="mt-1 px-5 py-3 rounded-full text-sm font-semibold text-center bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}